<template>
  <FormUploadDropTarget
    :uploadAvailable="canUpdate"
     @filesDropped="$refs.documentUpload.filesDroppedFromTarget($event)"
    class="attached-document-list">
    <div>
      <FormButton v-if="canUpdate" class="btn btn-sm btn-default float-right" @click="triggerFileBrowse"><i class="far fa-plus"></i></FormButton>
      <ContentTitle>Documents</ContentTitle>
      <ContentSubTitle>All documents attached to this resource</ContentSubTitle>
    </div>

    <p v-if="!list || list.length == 0" class="text-muted text-center font-italic">No documents found</p>
    <div class="document-list">
      <div v-for="document in list" :key="document.id" class="document-container">
        <ActionDropMenu :tableAction="true" :forceDropdown="true" class="float-right">
          <DropdownMenuItem icon="fa-eye" @click="previewDocument(document)" :link="docURL(document)">Preview</DropdownMenuItem>
          <DropdownMenuItem icon="fa-download" :link="docURL(document)">Download</DropdownMenuItem>
          <DropdownMenuDivider v-if="canUpdate" />
          <DropdownMenuItem icon="fa-trash-alt" v-if="canUpdate" @click="deleteDocument(document)">Delete</DropdownMenuItem>
        </ActionDropMenu>

        <div class="row">
          <div class="col-auto p-0">
            <i :class="['far', docIconClass(document), 'document-icon']"></i>
          </div>
          <div class="col-10 p-0">
            <a :href="docURL(document)" target="_blank" @click.prevent="previewDocument(document)" class="document-title">{{document.name}}</a><br />
            <div class="row no-gutters document-properties" v-if="document.description">
              <div class="col-1 font-weight-bold">Description:</div>
              <div class="col-11">{{document.description}}</div>
            </div>
            <div class="row no-gutters document-properties">
              <div class="col-1 font-weight-bold">Type:</div>
              <div class="col-11">{{docTypeName(document)}}</div>
            </div>
            <div class="row no-gutters document-properties">
              <div class="col-1 font-weight-bold">Uploaded:</div>
              <div class="col-11">{{formatDateTime(document.createdAt)}}</div>
            </div>
            <div class="row no-gutters document-properties" v-if="document.user">
              <div class="col-1 font-weight-bold">User:</div>
              <div class="col-11"><LinkUser :id="document.user.id" :name="document.user.name" /></div>
            </div>
          </div>
        </div>
      </div>
      
      <FormGroup :form="docForm" class="mt-4">
        <span class="col-form-label float-left mr-2" v-if="hasUploadFiles">Upload:</span>
        <FormUpload ref="documentUpload" v-model="uploadFileList" field="uploadFileList" :showBrowseButton="false" class="float-left" />
        <FormButton class="btn btn-primary btn-sm float-left ml-2" type="submit" :loading="submitting" @click.prevent="onSubmitted" v-show="hasUploadFiles">Upload</FormButton>
      </FormGroup>

    </div>
  </FormUploadDropTarget>
</template>

<style scoped>
.document-list {
  margin-top: 1rem;
}
.document-list .document-container {
  margin-top: 0.75rem;
  border: 1px solid #efefef;
  padding: 8px 8px;
  border-radius: 8px;
}
.document-container .document-icon {
  font-size: 2rem;
  color: #c0c0c0;
  padding-right: 12px;
}
.document-container .document-title {
  font-size: 14px;
  font-weight: bold;
  color: #676a6c;
}
.document-container .document-title:hover {
  color: #337ab7;
}
.document-container .document-properties {
  font-size: 11px;
  margin: 4px 4px;
  color: #808080;
}

.attached-document-list {
  min-height: 150px;
}
</style>

<script>
import form from '@/mixins/form'
import UserFileMixin from '@/mixins/userFile'
import DocumentPreviewDialog from '@/components/dialog/documentPreviewDialog'

export default {
  name: 'DocumentList',
  mixins: [UserFileMixin, form],
  props: {
    linkType: String,
    linkId: [String, Number],
    permissionResource: String
  },
  data() {
    return {
      list: [],
      uploadFileList: [],
      docForm: {},
      submitting: false
    }
  },
  watch: {
    linkType: function(newVal, oldVal) {
      this.requestData()
    },
    linkId: function(newVal, oldVal) {
      this.requestData()
    }
  },
  computed: {
    canUpdate() {
      return this.userHasUpdatePermission(this.permissionResource)
    },
    hasUploadFiles() {
      return this.uploadFileList.length > 0
    }
  },
  mounted() {
    this.requestData()
  },
  methods: {
    requestData() {
      if(this.linkType == null || this.linkId == null)
        return

      this.$apiService.coreService.getUserFiles(this.linkType, this.linkId).then(response => {
        this.$data.list = response.data;
      })
    },
    deleteDocument(document) {
      this.confirmDeleteMessage('Delete Document?', 'Are you sure you want to delete this document? This action cannot be undone!')
      .then(success => {
        if(success) {
          this.$apiService.coreService.deleteUserFile(document.id).then(response => {
            this.successToast('Document Deleted')
            this.requestData()
          })
        }
      })
    },
    docURL(document) {
      return document.url
    },
    docIconClass(document) {
      return this.iconForUserFile(document.mimeType)
    },
    docTypeName(document) {
      return this.descriptionForUserFileMimeType(document.mimeType, document.name)
    },
    previewDocument(document) {
      this.displayModal(DocumentPreviewDialog, {
        documentList: this.list,
        currentDocumentId: document.id
      })
      .then(() => { })
    },
    triggerFileBrowse() {
      this.$refs.documentUpload.onBrowse()
    },
    onSubmitted() {
      this.submitting = true
      this.docForm = this.clearFormResponse()

      this.$apiService.coreService.createUserFile({ linkType: this.$props.linkType, linkId: this.$props.linkId, uploadFileList: this.$data.uploadFileList })
        .then(() => {
          this.submitting = false
          this.$refs.documentUpload.clear()
          this.successToast('Document Added')
          this.requestData()
        })
        .catch(err => {
          this.submitting = false
          this.$data.docForm = this.processFormResponse(err)
        })
    }
  }
}
</script>